import * as React from "react"
import {
  Building2,
  CalendarDays,
  Clock,
  Globe,
  Loader2,
  Mic,
  Radio,
  RefreshCw,
  Send,
  Users,
  Gamepad2,
  Star,
  Plus,
  Pencil,
  Megaphone,
  X,
  Upload,
  Trash2,
  ImageIcon,
  ArrowRight,
} from "lucide-react"
import { useAuth, type AdminAgencyTarget, type AmaEvent, type GameEvent } from "@/contexts/AuthContext"
import { AdminCard } from "./AdminCard"
import { ICON_NAMES, getEventIcon } from "@/lib/iconMap"

type Feedback = { type: "success" | "error"; text: string } | null

function formatEventWhen(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function getAmaEventStatus(event: AmaEvent): "live" | "upcoming" | "ended" {
  const start = new Date(event.startsAt).getTime()
  const end = start + Math.max(1, event.durationMinutes || 0) * 60_000
  const now = Date.now()
  if (!Number.isFinite(start)) return "upcoming"
  if (now < start) return "upcoming"
  if (now <= end) return "live"
  return "ended"
}

const EVENT_TYPES = [
  { value: "action_challenge", label: "Action Challenge" },
  { value: "points_multiplier", label: "Points Multiplier" },
  { value: "raffle", label: "Raffle" },
  { value: "king_of_hill", label: "King of the Hill" },
  { value: "custom", label: "Custom" },
]

const EVENT_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "coming_soon", label: "Coming Soon" },
  { value: "live", label: "Live" },
  { value: "ended", label: "Ended" },
]

const REQUIREMENT_TYPES = [
  { value: "submit_deals", label: "Submit Deals" },
  { value: "earn_points", label: "Earn Points" },
  { value: "reach_rank", label: "Reach Rank" },
  { value: "login_streak", label: "Login Streak" },
  { value: "custom", label: "Custom" },
]

const REWARD_TYPES = [
  { value: "cash", label: "Cash" },
  { value: "points", label: "Points" },
  { value: "free_spins", label: "Free Spins" },
  { value: "raffle_tickets", label: "Raffle Tickets" },
  { value: "badge", label: "Badge" },
  { value: "custom", label: "Custom" },
]

// ─── Date Range Helpers ──────────────────────────────────────────────────────

function toLocalDatetimeString(date: Date): string {
  const y = date.getFullYear()
  const mo = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  const h = String(date.getHours()).padStart(2, "0")
  const mi = String(date.getMinutes()).padStart(2, "0")
  return `${y}-${mo}-${d}T${h}:${mi}`
}

function formatDatePreview(value: string): string {
  if (!value) return ""
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
}

function computeDuration(start: string, end: string): string {
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (ms <= 0) return "Invalid range"
  const totalMins = Math.floor(ms / 60000)
  const days = Math.floor(totalMins / 1440)
  const hours = Math.floor((totalMins % 1440) / 60)
  const mins = totalMins % 60
  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (mins > 0 && days === 0) parts.push(`${mins}m`)
  return parts.join(" ") || "< 1m"
}

const DATE_PRESETS: Array<{ label: string; getDates: () => { start: string; end: string } }> = [
  {
    label: "Next 24h",
    getDates: () => {
      const now = new Date()
      const end = new Date(now.getTime() + 24 * 3600000)
      return { start: toLocalDatetimeString(now), end: toLocalDatetimeString(end) }
    },
  },
  {
    label: "This Weekend",
    getDates: () => {
      const now = new Date()
      const day = now.getDay()
      const daysUntilFri = day <= 5 ? 5 - day : 6
      const fri = new Date(now)
      fri.setDate(now.getDate() + daysUntilFri)
      fri.setHours(18, 0, 0, 0)
      const sun = new Date(fri)
      sun.setDate(fri.getDate() + 2)
      sun.setHours(23, 59, 0, 0)
      return { start: toLocalDatetimeString(fri), end: toLocalDatetimeString(sun) }
    },
  },
  {
    label: "Next 7 Days",
    getDates: () => {
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      const end = new Date(now.getTime() + 7 * 24 * 3600000)
      end.setHours(23, 59, 0, 0)
      return { start: toLocalDatetimeString(now), end: toLocalDatetimeString(end) }
    },
  },
  {
    label: "Next 30 Days",
    getDates: () => {
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      const end = new Date(now.getTime() + 30 * 24 * 3600000)
      end.setHours(23, 59, 0, 0)
      return { start: toLocalDatetimeString(now), end: toLocalDatetimeString(end) }
    },
  },
  {
    label: "This Month",
    getDates: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0)
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59)
      return { start: toLocalDatetimeString(start), end: toLocalDatetimeString(end) }
    },
  },
]

// ─── Game Event Form Modal ───────────────────────────────────────────────────

interface GameEventFormState {
  title: string
  description: string
  eventType: string
  status: string
  isFeatured: boolean
  startDate: string
  endDate: string
  imageUrl: string
  detailImageUrl: string
  badge: string
  badgeColor: string
  iconName: string
  pointsMultiplier: string
  rewards: Array<{ type: string; amount: string; label: string }>
  actionRequirements: Array<{ type: string; target: string; metric: string }>
}

const EMPTY_FORM: GameEventFormState = {
  title: "",
  description: "",
  eventType: "action_challenge",
  status: "draft",
  isFeatured: false,
  startDate: "",
  endDate: "",
  imageUrl: "",
  detailImageUrl: "",
  badge: "Event",
  badgeColor: "#F37020",
  iconName: "trophy",
  pointsMultiplier: "",
  rewards: [],
  actionRequirements: [],
}

interface GameEventTemplate {
  id: string
  name: string
  description: string
  icon: string
  accentColor: string
  formState: GameEventFormState
}

const GAME_EVENT_TEMPLATES: GameEventTemplate[] = [
  {
    id: "king_of_hill",
    name: "King of the Hill",
    description: "Top deal submitter wins the crown",
    icon: "crown",
    accentColor: "#FFD700",
    formState: {
      title: "King of the Hill",
      description: "Submit the most deals to claim the throne! The agent with the highest deal count by the end of the event wins the grand prize.",
      eventType: "king_of_hill",
      status: "draft",
      isFeatured: false,
      startDate: "",
      endDate: "",
      imageUrl: "",
      detailImageUrl: "",
      badge: "KOTH",
      badgeColor: "#FFD700",
      iconName: "crown",
      pointsMultiplier: "",
      rewards: [{ type: "cash", amount: "50000", label: "$50K Cash Prize" }],
      actionRequirements: [{ type: "submit_deals", target: "50", metric: "deals" }],
    },
  },
  {
    id: "double_rewards",
    name: "Double Rewards Weekend",
    description: "2x points on all activity",
    icon: "zap",
    accentColor: "#32CD32",
    formState: {
      title: "Double Rewards Weekend",
      description: "Earn double points on every deal submitted this weekend! Stack your rewards and climb the leaderboard faster than ever.",
      eventType: "points_multiplier",
      status: "draft",
      isFeatured: false,
      startDate: "",
      endDate: "",
      imageUrl: "",
      detailImageUrl: "",
      badge: "2X POINTS",
      badgeColor: "#32CD32",
      iconName: "zap",
      pointsMultiplier: "2",
      rewards: [],
      actionRequirements: [],
    },
  },
  {
    id: "weekly_raffle",
    name: "Weekly Cash Raffle",
    description: "Submit deals to earn raffle entries",
    icon: "ticket",
    accentColor: "#9B59B6",
    formState: {
      title: "Weekly Cash Raffle",
      description: "Every 5 deals submitted earns you one raffle entry. The more you submit, the better your chances to win the cash prize!",
      eventType: "raffle",
      status: "draft",
      isFeatured: false,
      startDate: "",
      endDate: "",
      imageUrl: "",
      detailImageUrl: "",
      badge: "RAFFLE",
      badgeColor: "#9B59B6",
      iconName: "ticket",
      pointsMultiplier: "",
      rewards: [{ type: "cash", amount: "1000", label: "$1K Cash Prize" }],
      actionRequirements: [{ type: "submit_deals", target: "5", metric: "deals" }],
    },
  },
  {
    id: "deal_sprint",
    name: "Deal Sprint",
    description: "Race to submit 20 deals in 24 hours",
    icon: "target",
    accentColor: "#F37020",
    formState: {
      title: "Deal Sprint",
      description: "Can you hit 20 deals in just 24 hours? Complete the sprint challenge and earn a massive points bonus!",
      eventType: "action_challenge",
      status: "draft",
      isFeatured: false,
      startDate: "",
      endDate: "",
      imageUrl: "",
      detailImageUrl: "",
      badge: "SPRINT",
      badgeColor: "#F37020",
      iconName: "target",
      pointsMultiplier: "",
      rewards: [{ type: "points", amount: "5000", label: "5,000 Bonus Points" }],
      actionRequirements: [{ type: "submit_deals", target: "20", metric: "deals" }],
    },
  },
  {
    id: "streak_challenge",
    name: "Streak Challenge",
    description: "7-day login streak for bonus rewards",
    icon: "flame",
    accentColor: "#EF4444",
    formState: {
      title: "Streak Challenge",
      description: "Log in every day for 7 days straight to earn bonus points and raffle tickets. Don't break the chain!",
      eventType: "action_challenge",
      status: "draft",
      isFeatured: false,
      startDate: "",
      endDate: "",
      imageUrl: "",
      detailImageUrl: "",
      badge: "STREAK",
      badgeColor: "#EF4444",
      iconName: "flame",
      pointsMultiplier: "",
      rewards: [
        { type: "points", amount: "2500", label: "2,500 Bonus Points" },
        { type: "raffle_tickets", amount: "3", label: "3 Raffle Tickets" },
      ],
      actionRequirements: [{ type: "login_streak", target: "7", metric: "days" }],
    },
  },
]

function GameEventFormModal({
  open,
  onClose,
  onSave,
  editEvent,
}: {
  open: boolean
  onClose: () => void
  onSave: (data: GameEventFormState) => Promise<void>
  editEvent: GameEvent | null
}) {
  const { getPresignedUrl } = useAuth()
  const [form, setForm] = React.useState<GameEventFormState>(EMPTY_FORM)
  const [saving, setSaving] = React.useState(false)
  const [uploadingBanner, setUploadingBanner] = React.useState(false)
  const [uploadingDetail, setUploadingDetail] = React.useState(false)
  const [selectedTemplate, setSelectedTemplate] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    if (editEvent) {
      setForm({
        title: editEvent.title,
        description: editEvent.description,
        eventType: editEvent.eventType,
        status: editEvent.status,
        isFeatured: editEvent.isFeatured,
        startDate: editEvent.startDate ? new Date(editEvent.startDate).toISOString().slice(0, 16) : "",
        endDate: editEvent.endDate ? new Date(editEvent.endDate).toISOString().slice(0, 16) : "",
        imageUrl: editEvent.imageUrl || "",
        detailImageUrl: editEvent.detailImageUrl || "",
        badge: editEvent.badge,
        badgeColor: editEvent.badgeColor,
        iconName: editEvent.iconName,
        pointsMultiplier: editEvent.pointsMultiplier ? String(editEvent.pointsMultiplier) : "",
        rewards: editEvent.rewards.map((r) => ({ type: r.type, amount: String(r.amount), label: r.label })),
        actionRequirements: editEvent.actionRequirements.map((r) => ({ type: r.type, target: String(r.target), metric: r.metric })),
      })
    } else {
      setForm(EMPTY_FORM)
    }
    setSelectedTemplate(null)
  }, [open, editEvent])

  const handleFileUpload = async (field: "imageUrl" | "detailImageUrl", file: File) => {
    const setUploading = field === "imageUrl" ? setUploadingBanner : setUploadingDetail
    setUploading(true)
    try {
      const presigned = await getPresignedUrl(file.name, file.type)
      if (!presigned.success || !presigned.uploadUrl || !presigned.fileUrl) {
        setUploading(false)
        return
      }
      await fetch(presigned.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } })
      setForm((prev) => ({ ...prev, [field]: presigned.fileUrl! }))
    } catch (error) {
      console.error("Upload error:", error)
    }
    setUploading(false)
  }

  const handleSave = async () => {
    setSaving(true)
    await onSave(form)
    setSaving(false)
  }

  if (!open) return null

  const IconPreview = getEventIcon(form.iconName)

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-[#111117] rounded-2xl border border-[#2A2A45] shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A45]">
          <h3 className="text-sm font-semibold text-white/80 font-admin">
            {editEvent ? "Edit Game Event" : "Create Game Event"}
          </h3>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-[#1C1C32] border border-[#2A2A45] flex items-center justify-center hover:border-white/20 transition-colors">
            <X className="w-3.5 h-3.5 text-white/60" />
          </button>
        </div>

        {/* Form */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Template Picker — only shown when creating */}
          {!editEvent && (
            <div className="space-y-2">
              <span className="text-[10px] uppercase tracking-wider text-white/30 font-admin">Start from a template</span>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {/* Blank option */}
                <button
                  type="button"
                  onClick={() => { setSelectedTemplate(null); setForm(EMPTY_FORM) }}
                  className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-admin transition-all ${
                    selectedTemplate === null
                      ? "bg-white/10 border-white/30 text-white"
                      : "bg-[#0C0C12] border-[#2A2A45] text-white/40 hover:border-white/20 hover:text-white/60"
                  }`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Blank
                </button>
                {GAME_EVENT_TEMPLATES.map((t) => {
                  const TIcon = getEventIcon(t.icon)
                  const isActive = selectedTemplate === t.id
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => { setSelectedTemplate(t.id); setForm(t.formState) }}
                      className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-admin transition-all ${
                        isActive
                          ? "text-white"
                          : "bg-[#0C0C12] border-[#2A2A45] text-white/40 hover:border-white/20 hover:text-white/60"
                      }`}
                      style={isActive ? { backgroundColor: `${t.accentColor}20`, borderColor: `${t.accentColor}60` } : undefined}
                    >
                      <TIcon className="w-3.5 h-3.5" style={isActive ? { color: t.accentColor } : undefined} />
                      <div className="text-left">
                        <div className="whitespace-nowrap">{t.name}</div>
                        <div className="text-[10px] text-white/30 whitespace-nowrap">{t.description}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Title & Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-white/40 font-admin space-y-1">
              Title *
              <input
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                maxLength={200}
                placeholder="King of the Hill"
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
              />
            </label>
            <label className="text-xs text-white/40 font-admin space-y-1">
              Event Type *
              <select
                value={form.eventType}
                onChange={(e) => setForm((p) => ({ ...p, eventType: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 outline-none focus:border-[#F37020]/40 transition-colors"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Status & Featured */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-white/40 font-admin space-y-1">
              Status
              <select
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 outline-none focus:border-[#F37020]/40 transition-colors"
              >
                {EVENT_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </label>
            <div className="text-xs text-white/40 font-admin space-y-1">
              <span>Featured Event</span>
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] cursor-pointer hover:border-white/10 transition-colors">
                <input
                  type="checkbox"
                  checked={form.isFeatured}
                  onChange={(e) => setForm((p) => ({ ...p, isFeatured: e.target.checked }))}
                  className="accent-[#FFD700]"
                />
                <Star className="w-3.5 h-3.5 text-[#FFD700]" />
                <span className="text-sm text-white/60">Featured (replaces banner)</span>
              </label>
              {form.isFeatured && (
                <p className="text-[10px] text-[#FFD700]/70 mt-1">Only 1 event can be featured at a time. This will un-feature any other event.</p>
              )}
            </div>
          </div>

          {/* Date Range */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/40 font-admin">Date Range</span>
              {(form.startDate || form.endDate) && (
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, startDate: "", endDate: "" }))}
                  className="text-[10px] text-white/30 hover:text-red-400 transition-colors font-admin"
                >
                  Clear dates
                </button>
              )}
            </div>

            {/* Quick Presets */}
            <div className="flex flex-wrap gap-1.5">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => {
                    const { start, end } = preset.getDates()
                    setForm((p) => ({ ...p, startDate: start, endDate: end }))
                  }}
                  className="px-2.5 py-1 rounded-md bg-[#1C1C32] border border-[#2A2A45] text-[10px] text-white/50 hover:border-[#F37020]/40 hover:text-white/80 hover:bg-[#F37020]/5 transition-all font-admin"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Start / End Inputs */}
            <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
              {/* Start */}
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-white/25 font-admin">From</span>
                <div className="relative group">
                  <CalendarDays className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20 group-focus-within:text-[#F37020]/60 transition-colors" />
                  <input
                    type="datetime-local"
                    value={form.startDate}
                    onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 outline-none focus:border-[#F37020]/40 focus:bg-[#0E0E16] transition-all [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-40 [&::-webkit-calendar-picker-indicator]:hover:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                </div>
                {form.startDate && (
                  <p className="text-[10px] text-white/25 font-admin pl-1">{formatDatePreview(form.startDate)}</p>
                )}
              </div>

              {/* Arrow */}
              <div className="flex items-center justify-center pb-5">
                <div className="w-8 h-8 rounded-full bg-[#1C1C32] border border-[#2A2A45] flex items-center justify-center">
                  <ArrowRight className="w-3.5 h-3.5 text-white/30" />
                </div>
              </div>

              {/* End */}
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-white/25 font-admin">To</span>
                <div className="relative group">
                  <CalendarDays className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20 group-focus-within:text-[#F37020]/60 transition-colors" />
                  <input
                    type="datetime-local"
                    value={form.endDate}
                    onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 outline-none focus:border-[#F37020]/40 focus:bg-[#0E0E16] transition-all [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-40 [&::-webkit-calendar-picker-indicator]:hover:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                </div>
                {form.endDate && (
                  <p className="text-[10px] text-white/25 font-admin pl-1">{formatDatePreview(form.endDate)}</p>
                )}
              </div>
            </div>

            {/* Duration Badge */}
            {form.startDate && form.endDate && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#F37020]/5 border border-[#F37020]/10 w-fit">
                <Clock className="w-3 h-3 text-[#F37020]/50" />
                <span className="text-[10px] text-[#F37020]/70 font-admin font-medium">
                  Duration: {computeDuration(form.startDate, form.endDate)}
                </span>
              </div>
            )}
          </div>

          {/* Description */}
          <label className="block text-xs text-white/40 font-admin space-y-1">
            Description
            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={3}
              maxLength={2000}
              placeholder="Describe the event, rules, and how to participate..."
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 resize-y transition-colors"
            />
          </label>

          {/* Badge & Icon */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="text-xs text-white/40 font-admin space-y-1">
              Badge Text
              <input
                value={form.badge}
                onChange={(e) => setForm((p) => ({ ...p, badge: e.target.value }))}
                maxLength={30}
                placeholder="Crown"
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
              />
            </label>
            <label className="text-xs text-white/40 font-admin space-y-1">
              Badge Color
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.badgeColor}
                  onChange={(e) => setForm((p) => ({ ...p, badgeColor: e.target.value }))}
                  className="w-8 h-8 rounded border border-[#2A2A45] bg-transparent cursor-pointer"
                />
                <input
                  value={form.badgeColor}
                  onChange={(e) => setForm((p) => ({ ...p, badgeColor: e.target.value }))}
                  maxLength={20}
                  className="flex-1 px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 outline-none focus:border-[#F37020]/40 transition-colors"
                />
              </div>
            </label>
            <label className="text-xs text-white/40 font-admin space-y-1">
              Icon
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center border border-[#2A2A45]" style={{ backgroundColor: `${form.badgeColor}20` }}>
                  <IconPreview className="w-4 h-4" style={{ color: form.badgeColor }} />
                </div>
                <select
                  value={form.iconName}
                  onChange={(e) => setForm((p) => ({ ...p, iconName: e.target.value }))}
                  className="flex-1 px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 outline-none focus:border-[#F37020]/40 transition-colors"
                >
                  {ICON_NAMES.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            </label>
          </div>

          {/* Points Multiplier */}
          {form.eventType === "points_multiplier" && (
            <label className="block text-xs text-white/40 font-admin space-y-1">
              Points Multiplier
              <input
                type="number"
                min={1}
                max={10}
                step={0.5}
                value={form.pointsMultiplier}
                onChange={(e) => setForm((p) => ({ ...p, pointsMultiplier: e.target.value }))}
                placeholder="2"
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
              />
            </label>
          )}

          {/* Banner Image */}
          <div className="text-xs text-white/40 font-admin space-y-1">
            <span>Banner Image</span>
            <div className="flex items-center gap-3">
              {form.imageUrl ? (
                <div className="relative w-20 h-12 rounded-lg overflow-hidden border border-[#2A2A45]">
                  <img src={form.imageUrl} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setForm((p) => ({ ...p, imageUrl: "" }))}
                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded bg-black/60 flex items-center justify-center"
                  >
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              ) : null}
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/60 cursor-pointer hover:border-white/10 transition-colors">
                {uploadingBanner ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileUpload("imageUrl", file)
                    e.target.value = ""
                  }}
                />
              </label>
              <span className="text-[10px] text-white/25">or paste URL:</span>
              <input
                value={form.imageUrl}
                onChange={(e) => setForm((p) => ({ ...p, imageUrl: e.target.value }))}
                placeholder="https://..."
                className="flex-1 px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/60 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
              />
            </div>
          </div>

          {/* Detail Image */}
          <div className="text-xs text-white/40 font-admin space-y-1">
            <span>Detail Image (shown in modal)</span>
            <div className="flex items-center gap-3">
              {form.detailImageUrl ? (
                <div className="relative w-20 h-12 rounded-lg overflow-hidden border border-[#2A2A45]">
                  <img src={form.detailImageUrl} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setForm((p) => ({ ...p, detailImageUrl: "" }))}
                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded bg-black/60 flex items-center justify-center"
                  >
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              ) : null}
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/60 cursor-pointer hover:border-white/10 transition-colors">
                {uploadingDetail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileUpload("detailImageUrl", file)
                    e.target.value = ""
                  }}
                />
              </label>
              <input
                value={form.detailImageUrl}
                onChange={(e) => setForm((p) => ({ ...p, detailImageUrl: e.target.value }))}
                placeholder="https://..."
                className="flex-1 px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/60 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
              />
            </div>
          </div>

          {/* Action Requirements */}
          <div className="text-xs text-white/40 font-admin space-y-2">
            <div className="flex items-center justify-between">
              <span>Action Requirements</span>
              <button
                onClick={() => setForm((p) => ({ ...p, actionRequirements: [...p.actionRequirements, { type: "submit_deals", target: "10", metric: "" }] }))}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#0C0C12] border border-[#2A2A45] text-[10px] text-white/50 hover:text-white/80 transition-colors"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {form.actionRequirements.map((req, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  value={req.type}
                  onChange={(e) => {
                    const updated = [...form.actionRequirements]
                    updated[i] = { ...updated[i]!, type: e.target.value }
                    setForm((p) => ({ ...p, actionRequirements: updated }))
                  }}
                  className="px-2 py-1.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 outline-none"
                >
                  {REQUIREMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  value={req.target}
                  onChange={(e) => {
                    const updated = [...form.actionRequirements]
                    updated[i] = { ...updated[i]!, target: e.target.value }
                    setForm((p) => ({ ...p, actionRequirements: updated }))
                  }}
                  placeholder="Target"
                  className="w-20 px-2 py-1.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 outline-none"
                />
                <input
                  value={req.metric}
                  onChange={(e) => {
                    const updated = [...form.actionRequirements]
                    updated[i] = { ...updated[i]!, metric: e.target.value }
                    setForm((p) => ({ ...p, actionRequirements: updated }))
                  }}
                  placeholder="e.g. Submit 10 deals"
                  className="flex-1 px-2 py-1.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 placeholder:text-white/20 outline-none"
                />
                <button
                  onClick={() => setForm((p) => ({ ...p, actionRequirements: p.actionRequirements.filter((_, j) => j !== i) }))}
                  className="w-6 h-6 rounded-md bg-red-500/10 border border-red-500/20 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              </div>
            ))}
          </div>

          {/* Rewards */}
          <div className="text-xs text-white/40 font-admin space-y-2">
            <div className="flex items-center justify-between">
              <span>Rewards</span>
              <button
                onClick={() => setForm((p) => ({ ...p, rewards: [...p.rewards, { type: "cash", amount: "", label: "" }] }))}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#0C0C12] border border-[#2A2A45] text-[10px] text-white/50 hover:text-white/80 transition-colors"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {form.rewards.map((reward, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  value={reward.type}
                  onChange={(e) => {
                    const updated = [...form.rewards]
                    updated[i] = { ...updated[i]!, type: e.target.value }
                    setForm((p) => ({ ...p, rewards: updated }))
                  }}
                  className="px-2 py-1.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 outline-none"
                >
                  {REWARD_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min={0}
                  value={reward.amount}
                  onChange={(e) => {
                    const updated = [...form.rewards]
                    updated[i] = { ...updated[i]!, amount: e.target.value }
                    setForm((p) => ({ ...p, rewards: updated }))
                  }}
                  placeholder="Amount"
                  className="w-24 px-2 py-1.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 outline-none"
                />
                <input
                  value={reward.label}
                  onChange={(e) => {
                    const updated = [...form.rewards]
                    updated[i] = { ...updated[i]!, label: e.target.value }
                    setForm((p) => ({ ...p, rewards: updated }))
                  }}
                  placeholder="e.g. $50K Grand Prize"
                  className="flex-1 px-2 py-1.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 placeholder:text-white/20 outline-none"
                />
                <button
                  onClick={() => setForm((p) => ({ ...p, rewards: p.rewards.filter((_, j) => j !== i) }))}
                  className="w-6 h-6 rounded-md bg-red-500/10 border border-red-500/20 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#2A2A45]">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-[#2A2A45] text-xs text-white/50 hover:text-white/80 transition-colors font-admin"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.title.trim()}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors font-admin ${
              !saving && form.title.trim()
                ? "bg-[#F59E0B] text-black hover:bg-[#E88F07]"
                : "bg-white/[0.04] text-white/20 cursor-not-allowed"
            }`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {editEvent ? "Save Changes" : "Create Event"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Announce Modal ──────────────────────────────────────────────────────────

function AnnounceModal({
  open,
  onClose,
  event,
  onAnnounce,
}: {
  open: boolean
  onClose: () => void
  event: GameEvent | null
  onAnnounce: (eventId: string, opts: { content?: string; fileUrl?: string; fileType?: string; fileName?: string }) => Promise<void>
}) {
  const { getPresignedUrl } = useAuth()
  const [content, setContent] = React.useState("")
  const [mediaUrl, setMediaUrl] = React.useState("")
  const [mediaType, setMediaType] = React.useState("")
  const [mediaName, setMediaName] = React.useState("")
  const [uploading, setUploading] = React.useState(false)
  const [sending, setSending] = React.useState(false)

  React.useEffect(() => {
    if (open && event) {
      setContent("")
      setMediaUrl(event.imageUrl || "")
      setMediaType(event.imageUrl ? "image/jpeg" : "")
      setMediaName(event.imageUrl ? "event-banner.jpg" : "")
    }
  }, [open, event])

  const handleFileUpload = async (file: File) => {
    setUploading(true)
    try {
      const presigned = await getPresignedUrl(file.name, file.type)
      if (presigned.success && presigned.uploadUrl && presigned.fileUrl) {
        await fetch(presigned.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } })
        setMediaUrl(presigned.fileUrl)
        setMediaType(file.type)
        setMediaName(file.name)
      }
    } catch (error) {
      console.error("Upload error:", error)
    }
    setUploading(false)
  }

  const handleSend = async () => {
    if (!event) return
    setSending(true)
    await onAnnounce(event._id, {
      content: content.trim() || undefined,
      fileUrl: mediaUrl || undefined,
      fileType: mediaType || undefined,
      fileName: mediaName || undefined,
    })
    setSending(false)
    onClose()
  }

  if (!open || !event) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#111117] rounded-2xl border border-[#2A2A45] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A45]">
          <h3 className="text-sm font-semibold text-white/80 font-admin flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-[#F37020]" />
            Announce: {event.title}
          </h3>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-[#1C1C32] border border-[#2A2A45] flex items-center justify-center">
            <X className="w-3.5 h-3.5 text-white/60" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <label className="block text-xs text-white/40 font-admin space-y-1">
            Custom Message (optional — default auto-generated)
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Leave empty for auto-generated announcement..."
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 resize-y transition-colors"
            />
          </label>

          {/* Media */}
          <div className="text-xs text-white/40 font-admin space-y-1">
            <span>Attachment (image or video)</span>
            <div className="flex items-center gap-3">
              {mediaUrl && (
                <div className="relative w-16 h-10 rounded-lg overflow-hidden border border-[#2A2A45]">
                  <img src={mediaUrl} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => { setMediaUrl(""); setMediaType(""); setMediaName("") }}
                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded bg-black/60 flex items-center justify-center"
                  >
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              )}
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/60 cursor-pointer hover:border-white/10 transition-colors">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload
                <input
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileUpload(file)
                    e.target.value = ""
                  }}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#2A2A45]">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#2A2A45] text-xs text-white/50 hover:text-white/80 transition-colors font-admin">
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors font-admin ${
              !sending ? "bg-[#F37020] text-white hover:bg-[#E06010]" : "bg-white/[0.04] text-white/20 cursor-not-allowed"
            }`}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
            Blast to All Chats
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main AdminEvents Component ─────────────────────────────────────────────

export function AdminEvents() {
  const { getAmaEvents, createAdminAmaEvent, getAdminAgencyTargets, getAdminGameEvents, createAdminGameEvent, updateAdminGameEvent, announceGameEvent } = useAuth()

  // AMA events state
  const [amaEvents, setAmaEvents] = React.useState<AmaEvent[]>([])
  const [agencyTargets, setAgencyTargets] = React.useState<AdminAgencyTarget[]>([])
  const [loading, setLoading] = React.useState(true)
  const [refreshing, setRefreshing] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [feedback, setFeedback] = React.useState<Feedback>(null)

  // AMA form state
  const [title, setTitle] = React.useState("")
  const [description, setDescription] = React.useState("")
  const [hostName, setHostName] = React.useState("Oval Team")
  const [startsAtLocal, setStartsAtLocal] = React.useState("")
  const [durationMinutes, setDurationMinutes] = React.useState(60)
  const [joinUrl, setJoinUrl] = React.useState("")
  const [streamUrl, setStreamUrl] = React.useState("")
  const [targetMode, setTargetMode] = React.useState<"all" | "specific">("all")
  const [targetAgencyId, setTargetAgencyId] = React.useState("")

  // Game events state
  const [gameEvents, setGameEvents] = React.useState<GameEvent[]>([])
  const [gameEventFeedback, setGameEventFeedback] = React.useState<Feedback>(null)
  const [showGameEventForm, setShowGameEventForm] = React.useState(false)
  const [editingGameEvent, setEditingGameEvent] = React.useState<GameEvent | null>(null)
  const [announceEvent, setAnnounceEvent] = React.useState<GameEvent | null>(null)

  const loadData = React.useCallback(async (silent?: boolean) => {
    if (silent) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }

    const [eventsRes, agenciesRes, gameEventsRes] = await Promise.all([
      getAmaEvents(),
      getAdminAgencyTargets(),
      getAdminGameEvents(),
    ])

    if (eventsRes.success && eventsRes.events) {
      const sorted = [...eventsRes.events].sort(
        (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
      )
      setAmaEvents(sorted)
    } else if (!silent) {
      setFeedback({ type: "error", text: eventsRes.message || "Failed to load scheduled events." })
    }

    if (agenciesRes.success && agenciesRes.agencies) {
      setAgencyTargets(agenciesRes.agencies)
    }

    if (gameEventsRes.success && gameEventsRes.events) {
      setGameEvents(gameEventsRes.events)
    }

    if (silent) {
      setRefreshing(false)
    } else {
      setLoading(false)
    }
  }, [getAmaEvents, getAdminAgencyTargets, getAdminGameEvents])

  React.useEffect(() => {
    loadData()
  }, [loadData])

  // AMA event creation
  const canSubmit =
    !submitting &&
    title.trim().length > 0 &&
    hostName.trim().length > 0 &&
    startsAtLocal.trim().length > 0 &&
    joinUrl.trim().length > 0 &&
    (targetMode === "all" || targetAgencyId.length > 0)

  const handleCreate = async () => {
    if (!canSubmit) return

    const startsAt = new Date(startsAtLocal)
    if (Number.isNaN(startsAt.getTime())) {
      setFeedback({ type: "error", text: "Please choose a valid start date and time." })
      return
    }

    setSubmitting(true)
    setFeedback(null)

    const result = await createAdminAmaEvent({
      title: title.trim(),
      description: description.trim() || undefined,
      hostName: hostName.trim(),
      startsAt: startsAt.toISOString(),
      durationMinutes,
      joinUrl: joinUrl.trim(),
      streamUrl: streamUrl.trim() || undefined,
      sendToAll: targetMode === "all",
      agencyId: targetMode === "specific" ? targetAgencyId : undefined,
    })

    if (result.success) {
      setFeedback({ type: "success", text: result.message || "Event scheduled successfully." })
      setTitle("")
      setDescription("")
      setStartsAtLocal("")
      setJoinUrl("")
      setStreamUrl("")
      if (targetMode === "specific") setTargetAgencyId("")
      await loadData(true)
    } else {
      setFeedback({ type: "error", text: result.message || "Failed to schedule event." })
    }

    setSubmitting(false)
  }

  // Game event save handler
  const handleGameEventSave = async (formData: GameEventFormState) => {
    const payload: any = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      eventType: formData.eventType,
      status: formData.status,
      isFeatured: formData.isFeatured,
      startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
      endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
      imageUrl: formData.imageUrl || null,
      detailImageUrl: formData.detailImageUrl || null,
      badge: formData.badge || "Event",
      badgeColor: formData.badgeColor || "#F37020",
      iconName: formData.iconName || "trophy",
      pointsMultiplier: formData.pointsMultiplier ? parseFloat(formData.pointsMultiplier) : null,
      rewards: formData.rewards.map((r) => ({ type: r.type, amount: parseFloat(r.amount) || 0, label: r.label })),
      actionRequirements: formData.actionRequirements.map((r) => ({ type: r.type, target: parseInt(r.target) || 0, metric: r.metric })),
    }

    let result
    if (editingGameEvent) {
      result = await updateAdminGameEvent(editingGameEvent._id, payload)
    } else {
      result = await createAdminGameEvent(payload)
    }

    if (result.success) {
      setGameEventFeedback({ type: "success", text: result.message || "Game event saved successfully." })
      setShowGameEventForm(false)
      setEditingGameEvent(null)
      await loadData(true)
    } else {
      setGameEventFeedback({ type: "error", text: result.message || "Failed to save game event." })
    }

    setTimeout(() => setGameEventFeedback(null), 4000)
  }

  const handleAnnounce = async (eventId: string, opts: { content?: string; fileUrl?: string; fileType?: string; fileName?: string }) => {
    const result = await announceGameEvent(eventId, opts)
    if (result.success) {
      setGameEventFeedback({ type: "success", text: result.message || "Announced successfully." })
    } else {
      setGameEventFeedback({ type: "error", text: result.message || "Failed to announce." })
    }
    setTimeout(() => setGameEventFeedback(null), 4000)
  }

  const now = Date.now()
  const upcomingCount = amaEvents.filter((event) => new Date(event.startsAt).getTime() > now).length
  const amaLiveCount = amaEvents.filter((event) => getAmaEventStatus(event) === "live").length

  const gameLiveCount = gameEvents.filter((e) => e.status === "live").length
  const gameComingSoonCount = gameEvents.filter((e) => e.status === "coming_soon").length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* ══════════ GAME EVENTS SECTION ══════════ */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Gamepad2 className="w-5 h-5 text-[#FFD700]" />
          <h2 className="text-base font-semibold text-white/80 font-admin">Game Events</h2>
        </div>

        {/* Game Event Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <AdminCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/20 flex items-center justify-center">
                <Gamepad2 className="w-5 h-5 text-[#FFD700]" />
              </div>
              <div>
                <p className="text-xs text-white/35 font-admin">Total Events</p>
                <p className="text-xl font-semibold text-white font-admin">{gameEvents.length}</p>
              </div>
            </div>
          </AdminCard>
          <AdminCard className="p-4" delay={0.03}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-400/10 border border-red-400/20 flex items-center justify-center">
                <Radio className="w-5 h-5 text-red-300" />
              </div>
              <div>
                <p className="text-xs text-white/35 font-admin">Live</p>
                <p className="text-xl font-semibold text-white font-admin">{gameLiveCount}</p>
              </div>
            </div>
          </AdminCard>
          <AdminCard className="p-4" delay={0.06}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-300" />
              </div>
              <div>
                <p className="text-xs text-white/35 font-admin">Coming Soon</p>
                <p className="text-xl font-semibold text-white font-admin">{gameComingSoonCount}</p>
              </div>
            </div>
          </AdminCard>
        </div>

        {/* Game Event Feedback */}
        {gameEventFeedback && (
          <div
            className={`px-3 py-2 rounded-lg text-xs font-medium border ${
              gameEventFeedback.type === "success"
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                : "bg-red-500/10 text-red-400 border-red-500/20"
            }`}
          >
            {gameEventFeedback.text}
          </div>
        )}

        {/* Game Events List */}
        <AdminCard delay={0.1}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-white/80 font-admin">All Game Events</h3>
              <p className="text-xs text-white/35 mt-1 font-admin">
                Manage reward events, challenges, and multiplier promotions.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadData(true)}
                disabled={refreshing}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2A45] bg-[#0C0C12] text-xs text-white/70 hover:text-white transition-colors disabled:opacity-60 font-admin"
              >
                {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => { setEditingGameEvent(null); setShowGameEventForm(true) }}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F59E0B] text-black text-xs font-medium hover:bg-[#E88F07] transition-colors font-admin"
              >
                <Plus className="w-3.5 h-3.5" />
                New Event
              </button>
            </div>
          </div>

          {gameEvents.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-[#2A2A45] bg-[#0C0C12]/70 p-6 text-center">
              <Gamepad2 className="w-6 h-6 text-white/25 mx-auto mb-2" />
              <p className="text-sm text-white/50 font-admin">No game events yet</p>
              <p className="text-xs text-white/25 mt-1 font-admin">
                Create one to get started with rewards and challenges.
              </p>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {gameEvents.map((event) => {
                const Icon = getEventIcon(event.iconName)
                return (
                  <div
                    key={event._id}
                    className="rounded-xl border border-[#1E1E2A] bg-[#0C0C12]/80 p-4"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center border" style={{ backgroundColor: `${event.badgeColor}15`, borderColor: `${event.badgeColor}30` }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: event.badgeColor }} />
                          </div>
                          <p className="text-sm text-white font-semibold font-admin">{event.title}</p>
                          {event.isFeatured && (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-admin text-[#FFD700] border-[#FFD700]/30 bg-[#FFD700]/10 flex items-center gap-1">
                              <Star className="w-2.5 h-2.5" /> Featured
                            </span>
                          )}
                          <span
                            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-admin ${
                              event.status === "live"
                                ? "text-red-300 border-red-400/30 bg-red-400/10"
                                : event.status === "coming_soon"
                                  ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10"
                                  : event.status === "ended"
                                    ? "text-white/40 border-white/10 bg-white/[0.03]"
                                    : "text-blue-300 border-blue-400/30 bg-blue-400/10"
                            }`}
                          >
                            {event.status.replace("_", " ")}
                          </span>
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-md text-white border border-[#2A2A45]/60"
                            style={{ backgroundColor: event.badgeColor }}
                          >
                            {event.badge}
                          </span>
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/45 font-admin">
                          {event.startDate && (
                            <span className="inline-flex items-center gap-1.5">
                              <CalendarDays className="w-3.5 h-3.5" />
                              {formatEventWhen(event.startDate)}
                            </span>
                          )}
                          {event.endDate && (
                            <span className="inline-flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" />
                              Ends: {formatEventWhen(event.endDate)}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" />
                            {event.participantCount} participants
                          </span>
                        </div>

                        {event.description && (
                          <p className="mt-2 text-xs text-white/50 font-admin leading-relaxed line-clamp-2">{event.description}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setEditingGameEvent(event); setShowGameEventForm(true) }}
                          className="px-3 py-1.5 rounded-lg bg-[#111117] border border-[#2A2A45] text-xs text-white/60 hover:text-white transition-colors font-admin inline-flex items-center gap-1.5"
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => setAnnounceEvent(event)}
                          className="px-3 py-1.5 rounded-lg bg-[#F37020]/10 border border-[#F37020]/20 text-xs text-[#F37020] hover:bg-[#F37020]/20 transition-colors font-admin inline-flex items-center gap-1.5"
                        >
                          <Megaphone className="w-3 h-3" /> Announce
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </AdminCard>
      </div>

      {/* ══════════ DIVIDER ══════════ */}
      <div className="border-t border-[#2A2A45]/50" />

      {/* ══════════ AMA/SESSION EVENTS SECTION ══════════ */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Mic className="w-5 h-5 text-[#F59E0B]" />
          <h2 className="text-base font-semibold text-white/80 font-admin">Event Sessions (AMA / Webinar)</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <AdminCard className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#F37020]/10 border border-[#F37020]/20 flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-[#F37020]" />
              </div>
              <div>
                <p className="text-xs text-white/35 font-admin">Scheduled</p>
                <p className="text-xl font-semibold text-white font-admin">{amaEvents.length}</p>
              </div>
            </div>
          </AdminCard>
          <AdminCard className="p-4" delay={0.03}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-300" />
              </div>
              <div>
                <p className="text-xs text-white/35 font-admin">Upcoming</p>
                <p className="text-xl font-semibold text-white font-admin">{upcomingCount}</p>
              </div>
            </div>
          </AdminCard>
          <AdminCard className="p-4" delay={0.06}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-400/10 border border-red-400/20 flex items-center justify-center">
                <Radio className="w-5 h-5 text-red-300" />
              </div>
              <div>
                <p className="text-xs text-white/35 font-admin">Live Now</p>
                <p className="text-xl font-semibold text-white font-admin">{amaLiveCount}</p>
              </div>
            </div>
          </AdminCard>
        </div>

        <AdminCard delay={0.1}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-white/80 font-admin">Create & Schedule Session</h3>
              <p className="text-xs text-white/35 mt-1 font-admin">
                Schedules an event session and posts the announcement to chat.
              </p>
            </div>
            <button
              onClick={() => loadData(true)}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2A45] bg-[#0C0C12] text-xs text-white/70 hover:text-white transition-colors disabled:opacity-60 font-admin"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-white/40 font-admin space-y-1">
              Event Title
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} placeholder="Top Producer AMA"
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors" />
            </label>
            <label className="text-xs text-white/40 font-admin space-y-1">
              Host Name
              <input value={hostName} onChange={(e) => setHostName(e.target.value)} maxLength={120} placeholder="Agency Growth Team"
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors" />
            </label>
            <label className="text-xs text-white/40 font-admin space-y-1">
              Start Date & Time
              <input type="datetime-local" value={startsAtLocal} onChange={(e) => setStartsAtLocal(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 outline-none focus:border-[#F37020]/40 transition-colors" />
            </label>
            <label className="text-xs text-white/40 font-admin space-y-1">
              Duration
              <select value={durationMinutes} onChange={(e) => setDurationMinutes(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 outline-none focus:border-[#F37020]/40 transition-colors">
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
                <option value={90}>90 minutes</option>
                <option value={120}>120 minutes</option>
              </select>
            </label>
          </div>

          <label className="mt-3 block text-xs text-white/40 font-admin space-y-1">
            Join URL
            <input value={joinUrl} onChange={(e) => setJoinUrl(e.target.value)} maxLength={800} placeholder="https://zoom.us/..."
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors" />
          </label>
          <label className="mt-3 block text-xs text-white/40 font-admin space-y-1">
            Stream URL (optional)
            <input value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)} maxLength={800} placeholder="https://youtube.com/watch?v=... or https://twitch.tv/..."
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors" />
          </label>
          <label className="mt-3 block text-xs text-white/40 font-admin space-y-1">
            Description (optional)
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={1200} placeholder="What will this session cover?"
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#F37020]/40 resize-y transition-colors" />
          </label>

          <div className="mt-3 flex flex-wrap gap-3">
            <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2A45] bg-[#0C0C12] hover:border-white/10 transition-colors">
              <input type="radio" name="admin-event-target" checked={targetMode === "all"} onChange={() => setTargetMode("all")} className="accent-[#F37020]" />
              <Globe className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-xs font-medium text-white/60 font-admin">All Agencies</span>
            </label>
            <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2A45] bg-[#0C0C12] hover:border-white/10 transition-colors">
              <input type="radio" name="admin-event-target" checked={targetMode === "specific"} onChange={() => setTargetMode("specific")} className="accent-[#F37020]" />
              <Building2 className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs font-medium text-white/60 font-admin">Specific Agency</span>
            </label>
          </div>

          {targetMode === "specific" && (
            <select value={targetAgencyId} onChange={(e) => setTargetAgencyId(e.target.value)}
              className="mt-3 w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 outline-none focus:border-[#F37020]/40 transition-colors">
              <option value="">Select an agency</option>
              {agencyTargets.map((agency) => (
                <option key={agency.agencyId} value={agency.agencyId}>
                  {agency.agencyName} ({agency.agencyId})
                </option>
              ))}
            </select>
          )}

          {feedback && (
            <div className={`mt-3 px-3 py-2 rounded-lg text-xs font-medium border ${
              feedback.type === "success" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-red-500/10 text-red-400 border-red-500/20"
            }`}>
              {feedback.text}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-[11px] text-white/25 font-admin">{title.length}/140 title characters</div>
            <button onClick={handleCreate} disabled={!canSubmit}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors font-admin ${
                canSubmit ? "bg-[#F59E0B] text-black hover:bg-[#E88F07]" : "bg-white/[0.04] text-white/20 cursor-not-allowed"
              }`}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Schedule Session
            </button>
          </div>
        </AdminCard>

        {/* Scheduled Sessions List */}
        <AdminCard delay={0.15}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-white/80 font-admin">Scheduled Sessions</h3>
              <p className="text-xs text-white/35 mt-1 font-admin">Event sessions available in the dashboard.</p>
            </div>
            <div className="inline-flex items-center gap-2 text-xs text-white/35 font-admin">
              <Users className="w-3.5 h-3.5" />
              {agencyTargets.length} agency targets available
            </div>
          </div>

          {amaEvents.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-[#2A2A45] bg-[#0C0C12]/70 p-6 text-center">
              <CalendarDays className="w-6 h-6 text-white/25 mx-auto mb-2" />
              <p className="text-sm text-white/50 font-admin">No scheduled sessions yet</p>
              <p className="text-xs text-white/25 mt-1 font-admin">Create one above to populate the Events tab.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {amaEvents.map((event) => {
                const status = getAmaEventStatus(event)
                return (
                  <div key={event._id} className="rounded-xl border border-[#1E1E2A] bg-[#0C0C12]/80 p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="w-7 h-7 rounded-lg bg-[#F59E0B]/15 border border-[#F59E0B]/20 flex items-center justify-center">
                            <Mic className="w-3.5 h-3.5 text-[#F59E0B]" />
                          </div>
                          <p className="text-sm text-white font-semibold font-admin">{event.title}</p>
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-admin ${
                            status === "live" ? "text-red-300 border-red-400/30 bg-red-400/10"
                              : status === "upcoming" ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10"
                                : "text-white/40 border-white/10 bg-white/[0.03]"
                          }`}>
                            {status}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/45 font-admin">
                          <span className="inline-flex items-center gap-1.5">
                            <CalendarDays className="w-3.5 h-3.5" />
                            {formatEventWhen(event.startsAt)}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            {event.durationMinutes} min
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" />
                            {event.sendToAll ? "All agencies" : (event.agencyName || "Specific agency")}
                          </span>
                        </div>
                        <p className="mt-2 text-xs text-white/60 font-admin">Host: {event.hostName}</p>
                        {event.description && <p className="mt-2 text-sm text-white/60 font-admin leading-relaxed">{event.description}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <a href={event.joinUrl} target="_blank" rel="noreferrer"
                          className="px-3 py-1.5 rounded-lg bg-[#111117] border border-[#2A2A45] text-xs text-white/70 hover:text-white transition-colors font-admin">
                          Open Join URL
                        </a>
                        {event.streamUrl && (
                          <a href={event.streamUrl} target="_blank" rel="noreferrer"
                            className="px-3 py-1.5 rounded-lg bg-[#111117] border border-[#2A2A45] text-xs text-white/50 hover:text-white/80 transition-colors font-admin">
                            Open Stream URL
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </AdminCard>
      </div>

      {/* ══════════ MODALS ══════════ */}
      <GameEventFormModal
        open={showGameEventForm}
        onClose={() => { setShowGameEventForm(false); setEditingGameEvent(null) }}
        onSave={handleGameEventSave}
        editEvent={editingGameEvent}
      />
      <AnnounceModal
        open={!!announceEvent}
        onClose={() => setAnnounceEvent(null)}
        event={announceEvent}
        onAnnounce={handleAnnounce}
      />
    </div>
  )
}
